import { useState, useEffect, useMemo } from 'react';
import {
  getAnalytics,
  type AnalyticsFilter,
  type AnalyticsResponse,
} from '../../services/api/analytics';
import { MONTH_NAMES, type DailyTotal } from './analytics.constants';
import { AnalyticsFilterBar } from './AnalyticsFilterBar';
import { AnalyticsKpiCards } from './AnalyticsKpiCards';
import { AnalyticsHandoffCards } from './AnalyticsHandoffCards';
import { HANDOFF_INBOX_ENABLED } from 'config/features';
import { AnalyticsBarChart } from './AnalyticsBarChart';
import { AnalyticsChannelBreakdown } from './AnalyticsChannelBreakdown';
import { AnalyticsQualityCards } from './AnalyticsQualityCards';
import { AnalyticsIntentBreakdown } from './AnalyticsIntentBreakdown';
import { AnalyticsCostCards } from './AnalyticsCostCards';
import { AnalyticsLlmRunsCards } from './AnalyticsLlmRunsCards';
import { AnalyticsAnalystPanel } from './AnalyticsAnalystPanel';

export const AnalyticsPage = () => {
  const [filter, setFilter] = useState<AnalyticsFilter>({ mode: 'days', days: 30 });
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getAnalytics(filter);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Okänt fel');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [filter]);

  const dailyTotals = useMemo<DailyTotal[]>(() => {
    if (!data) return [];
    const map = new Map<string, DailyTotal>();
    for (const row of data.daily) {
      if (!map.has(row.date)) map.set(row.date, { date: row.date, total: 0, byChannel: {} });
      const day = map.get(row.date)!;
      day.total += row.count;
      day.byChannel[row.channel] = (day.byChannel[row.channel] || 0) + row.count;
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const maxTotal = useMemo(() => Math.max(...dailyTotals.map((d) => d.total), 1), [dailyTotals]);
  const channels = useMemo(() => Object.keys(data?.totals.byChannel ?? {}), [data]);
  const avgPerDay =
    data && dailyTotals.length > 0 ? Math.round(data.totals.total / dailyTotals.length) : 0;
  const topDay =
    dailyTotals.length > 0
      ? dailyTotals.reduce((best, d) => (d.total > best.total ? d : best))
      : null;

  const periodLabel = useMemo(() => {
    if (filter.mode === 'month') return `${MONTH_NAMES[filter.month - 1]} ${filter.year}`;
    if (filter.mode === 'year') return `Helaret ${filter.year}`;
    return `Senaste ${filter.days} dagarna`;
  }, [filter]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });

  const handleQuickFilter = (days: number) => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setFilter({ mode: 'days', days });
  };

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setFilter({ mode: 'year', year });
  };

  const handleMonthSelect = (month: number) => {
    if (!selectedYear) return;
    setSelectedMonth(month);
    setFilter({ mode: 'month', year: selectedYear, month });
  };

  return (
    <section className="space-y-6">
      <AnalyticsFilterBar
        filter={filter}
        availableYears={data?.availableYears ?? []}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        onQuickFilter={handleQuickFilter}
        onYearSelect={handleYearSelect}
        onMonthSelect={handleMonthSelect}
        periodLabel={periodLabel}
        loading={loading}
      />

      <AnalyticsKpiCards
        data={data}
        loading={loading}
        dailyTotals={dailyTotals}
        avgPerDay={avgPerDay}
        topDay={topDay}
        periodLabel={periodLabel}
        formatDate={formatDate}
        channels={channels}
      />

      {HANDOFF_INBOX_ENABLED ? <AnalyticsHandoffCards /> : null}

      <AnalyticsBarChart
        dailyTotals={dailyTotals}
        channels={channels}
        maxTotal={maxTotal}
        loading={loading}
        error={error}
        formatDate={formatDate}
      />

      {data && !loading && channels.length > 0 && (
        <AnalyticsChannelBreakdown data={data} channels={channels} />
      )}

      <AnalyticsQualityCards data={data} loading={loading} />

      <AnalyticsIntentBreakdown data={data} loading={loading} />

      <AnalyticsCostCards data={data} loading={loading} />

      <AnalyticsLlmRunsCards data={data} loading={loading} />

      <AnalyticsAnalystPanel filter={filter} periodLabel={periodLabel} />
    </section>
  );
};
